import React, { useState } from "react";
import { BlockTitle } from "@blocks/Blocks";
import Ad from "@components/Advertisement/Ad";
import Container from "@components/Container/Container";
import { GADataProps } from "@components/GAData/GAData";
import Column from "@components/Grid/Column";
import Row from "@components/Grid/Row";
import OptimisedImage from "@components/OptimisedImage";
import EventsAwardsHeader from "@events-awards/components/EventAwardsHeader";
import EventsAwardsFooter from "@events-awards/components/EventsAwardsFooter";
import EventsAwardsNavigationBar from "@events-awards/components/EventsAwardsNavigationBarComponents/EventAwardsNavigationBar";
import { ImageModal } from "@events-awards/EventsAwardsGalleryYear/components/EventsAwardsGalleryImageModal";
import { EventsAwardsGalleryYearPageData } from "@events-awards/EventsAwardsGalleryYear/utils/types";
import { getAllImages } from "@events-awards/utils/helpers";
import { useInjectClientData } from "@hooks/useInjectClientData";
import { ImagePresets } from "@routes/Article/components/ArticleThumbnail/utils/enums";
import { CaaSImageFilters } from "@typings/OptimisedImage";

/**
 * Component to render gallery year page for event and awards
 */
const EventsAwardsGalleryYearPageContent = ({
  articles,
  awardType,
  gaProps,
}: EventsAwardsGalleryYearPageData & {
  gaProps: GADataProps;
}): React.ReactElement => {
  useInjectClientData({ gaProps });

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const allImages = getAllImages(articles);
  const imageFilters: CaaSImageFilters[] = [
    { w: ImagePresets.SMALL, dpr: 1, f: "webp" },
  ];

  const handleImageClick = (imageUrl: string): void => {
    setSelectedImage(imageUrl);
    setModalOpen(true);
  };

  return (
    <div
      className="min-h-screen w-full bg-gray-50"
      data-testid="events-awards-gallery-year-page-content-component"
    >
      <EventsAwardsHeader />

      <Container>
        <Row rootClassName="justify-center">
          <Column rootClassName="w-full mb-8">
            <EventsAwardsNavigationBar awardType={awardType} />

            <div className="py-8">
              <BlockTitle
                text={articles?.[0]?.title}
                rootClassName="text-5xl md:text-8xl lg:text-11xl font-medium text-left mb-4 font-public-sans"
              />

              <div className="grid grid-cols-2 gap-6 md:grid-cols-2 lg:grid-cols-4">
                {allImages.map((image) => {
                  const imageUrl =
                    image.relation?.fields?.["landscape-caas"]?.url;

                  if (typeof imageUrl === "undefined") return <></>;

                  return (
                    <div
                      key={image.id}
                      className="cursor-pointer overflow-hidden bg-white shadow-sm"
                      onClick={() => {
                        handleImageClick(imageUrl);
                      }}
                    >
                      <OptimisedImage
                        src={imageUrl}
                        loading="lazy"
                        alt={
                          image.fields?.find((f) => f.name === "caption")
                            ?.value || ""
                        }
                        filters={imageFilters}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </Column>
        </Row>
      </Container>

      <EventsAwardsFooter awardType={awardType} />

      <ImageModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
        }}
        image={selectedImage}
      />

      <Ad adType="catfish" />

      <Ad adType="prestitial" />
    </div>
  );
};

export default EventsAwardsGalleryYearPageContent;
